/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef } from 'react';
import { bool, node } from 'prop-types';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import withScript from '../../../../lib/scriptLoader';
import { cardIslePluginConfig } from '../../../../state/ducks/Checkout/ducks/Recipient/ducks/CardIsle/CardIsle-Helpers';
import { addCardIsleGiftCardToCart } from '../../../../state/ducks/Recipient/ducks/CardIsle/CardIsle-Actions';
import LoadCardIslePDPLib from './LoadCardIslePDPLib';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import SiteLoader from '../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { setCardIsleData } from '../../../../state/ducks/App/App-Actions';
import { getCardIsleData } from '../../../../state/ducks/App/App-Selectors';
import { isCardIsleOrigin } from './cardIsleMessageUtils';

const { sdkURL } = cardIslePluginConfig;

const LoadCardIslePdP = ({ children, scriptLoaded }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const slotRef = useRef(null);
    const brandDomainName = useSelector(getBrandName);
    const cardIsleData = useSelector(getCardIsleData);
    const isCardIslePdpAbTestEnabled = useSelector(getFeatureFlag('is-card-isle-pdp-ab-test-enabled'));
    const moveCardIsleBlockIntoSlot = () => {
        if (typeof document === 'undefined' || !slotRef.current) {
            return;
        }

        const cardIsleBlock = document.getElementById('card-isle-pdp-block');
        if (cardIsleBlock && cardIsleBlock.parentElement !== slotRef.current) {
            slotRef.current.appendChild(cardIsleBlock);
        }
    };

    useEffect(() => {
        const handler = (e) => {
            const btn = e.target.closest('#card-isle-remove-button');
            if (btn) {
                dispatch(setCardIsleData({}));
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);
    useEffect(() => {
        if (Object.keys(cardIsleData)?.length > 0) {
            dispatch(setCardIsleData({}));
        }
    }, []);
    const cardIsleReceiveMessage = (event) => {
        // verify that message came from Card Isle
        if (!isCardIsleOrigin(event.origin)) {
            return;
        }

        if (typeof event.data.status !== 'undefined' && typeof document !== 'undefined') {
            if (event.data.status === 'success') {
                const giftCards = event.data.gift_cards || [];
                const cardIslecomponents = giftCards.map((giftCard) => ({
                    brandCode: giftCard.brandCode,
                    componentType: 'GIFTCARD',
                    productCode: giftCard.sku,
                    quantity: 1,
                    attributes: [
                        {
                            attributeName: 'thumbnail',
                            attributeValue: giftCard.imageSrc,
                        },
                        {
                            attributeName: 'amount',
                            attributeValue: giftCard.amount,
                        },
                        {
                            attributeName: 'text',
                            attributeValue: giftCard.brand,
                        },
                    ],
                }));
                const payload = {
                    sku: event.data.greeting?.greetingSKU,
                    brandCode: event.data.greeting?.greetingBrandCode || '1012',
                    greetingMessage: event.data.card_text,
                    greetingCardKey: event.data.pickup_code,
                    greetingCardImage: event.data.card_image,
                    greetingCardName: event.data.card_name,
                    greetingCardType: 'CI',
                    greetingThumbNail: event.data.card_thumbnail,
                    components: cardIslecomponents,
                };
                dispatch(addCardIsleGiftCardToCart(payload, history));
            }
            if (event?.data?.status === 'pdp_addon') {
                dispatch(setCardIsleData(event?.data));
            }
        }
    };
    useEffect(() => {
        if (window.addEventListener) {
            window.addEventListener('message', cardIsleReceiveMessage, false);
        } else if (window.attachEvent) {
            window.attachEvent('onmessage', cardIsleReceiveMessage);
        }
        return (() => {
            if (typeof window !== 'undefined') {
                if (window.addEventListener && window.initiate_card_isle_widget) {
                    window.removeEventListener('message', cardIsleReceiveMessage, false);
                } else if (window.detachEvent) {
                    window.detachEvent('onmessage', cardIsleReceiveMessage);
                }
            }
        });
    }, []);

    const { loading, variables, data } = useSalesforcePersonalizedContentQuery({
        queryName: 'pdpCardIsleShowOrHide',
        interactionName: `${brandDomainName} - Get Campaign - Card Isle PDP`,
        featureFlag: isCardIslePdpAbTestEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const pdpCardIsleAbTestUserGroup = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isPdpCardIsleVariant = pdpCardIsleAbTestUserGroup?.toLowerCase() === 'test'; // true if variant false if control

    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: 'pdp' },
    });

    useEffect(() => {
        if (typeof data !== 'undefined' && !window.wuPdpCardIsleShowOrHideEventFired && pdpCardIsleAbTestUserGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Card Isle On PDP',
                eventLabel: isPdpCardIsleVariant ? 'Variant' : 'Control',
                nonInteraction: true,
            };
            dispatch(trackEvent(eventParam));
            window.wuPdpCardIsleShowOrHideEventFired = true;
        }
        return () => {
            window.wuPdpCardIsleShowOrHideEventFired = false;
        };
    }, [pdpCardIsleAbTestUserGroup]);

    useEffect(() => {
        if (!scriptLoaded || typeof document === 'undefined' || (isCardIslePdpAbTestEnabled && !isPdpCardIsleVariant)) {
            return undefined;
        }

        moveCardIsleBlockIntoSlot();

        const observer = new MutationObserver(() => {
            moveCardIsleBlockIntoSlot();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [scriptLoaded, isCardIslePdpAbTestEnabled, isPdpCardIsleVariant]);

    if (loading || variables?.skip) {
        return <SiteLoader />;
    }

    if (!scriptLoaded) return null;

    if (isCardIslePdpAbTestEnabled && !isPdpCardIsleVariant) return null;

    return (
        <>
            <LoadCardIslePDPLib />
            <div ref={slotRef} className="card-isle-pdp-slot" />
            {children}
        </>
    );
};

LoadCardIslePdP.propTypes = {
    children: node,
    scriptLoaded: bool.isRequired,
};

LoadCardIslePdP.defaultProps = {
    children: null,
};

export default withScript(sdkURL, false, false)(LoadCardIslePdP);
